import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';

import { SupabaseService } from '../../../core/services/supabase.service';
import { NotesRepository } from '../domain/notes.repository';
import {
  NoteCategory,
  NoteCategoryInput,
  NoteInput,
  PersonalNote
} from '../models/note.model';

interface NoteRow {
  id: number;
  title: string;
  content: string;
  category_id: number | null;
  note_date: string;
  archived_at: string | null;
  pinned_at: string | null;
  created_at: string;
  updated_at: string;
}

interface NoteCategoryRow {
  id: number;
  name: string;
  domain_id: number | null;
  created_at: string;
  updated_at: string;
}

const NOTE_COLUMNS = 'id,title,content,category_id,note_date,archived_at,pinned_at,created_at,updated_at';
const CATEGORY_COLUMNS = 'id,name,domain_id,created_at,updated_at';

@Injectable()
export class SupabaseNotesRepository extends NotesRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async getNotes(): Promise<PersonalNote[]> {
    const { data, error } = await this.client
      .from('notes')
      .select(NOTE_COLUMNS)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as NoteRow[]).map(row => this.mapNote(row));
  }

  async getNote(id: number): Promise<PersonalNote | null> {
    const { data, error } = await this.client.from('notes')
      .select(NOTE_COLUMNS).eq('id', id).maybeSingle();
    if (error) throw error;
    return data ? this.mapNote(data as NoteRow) : null;
  }

  async getCategories(): Promise<NoteCategory[]> {
    const { data, error } = await this.client
      .from('note_categories')
      .select(CATEGORY_COLUMNS)
      .order('name', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as NoteCategoryRow[]).map(row => this.mapCategory(row));
  }

  async createNote(input: NoteInput): Promise<PersonalNote> {
    const { data, error } = await this.client
      .from('notes')
      .insert({
        title: input.title,
        content: input.content,
        category_id: input.categoryId,
        note_date: input.noteDate
      })
      .select(NOTE_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapNote(data as NoteRow);
  }

  async updateNote(id: number, input: NoteInput): Promise<PersonalNote> {
    const { data, error } = await this.client
      .from('notes')
      .update({
        title: input.title,
        content: input.content,
        category_id: input.categoryId,
        note_date: input.noteDate
      })
      .eq('id', id)
      .select(NOTE_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapNote(data as NoteRow);
  }

  async setPinned(id: number, pinned: boolean): Promise<PersonalNote> {
    const { data, error } = await this.client
      .from('notes')
      .update({ pinned_at: pinned ? new Date().toISOString() : null })
      .eq('id', id)
      .select(NOTE_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapNote(data as NoteRow);
  }

  async setArchived(id: number, archived: boolean): Promise<PersonalNote> {
    const { data, error } = await this.client
      .from('notes')
      .update({ archived_at: archived ? new Date().toISOString() : null })
      .eq('id', id)
      .select(NOTE_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapNote(data as NoteRow);
  }

  async deleteNote(id: number): Promise<void> {
    const { error } = await this.client.from('notes').delete().eq('id', id);
    if (error) throw error;
  }

  async createCategory(input: NoteCategoryInput): Promise<NoteCategory> {
    const { data, error } = await this.client
      .from('note_categories')
      .insert({ name: input.name, domain_id: input.domainId })
      .select(CATEGORY_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapCategory(data as NoteCategoryRow);
  }

  async updateCategory(id: number, input: NoteCategoryInput): Promise<NoteCategory> {
    const { data, error } = await this.client
      .from('note_categories')
      .update({ name: input.name, domain_id: input.domainId })
      .eq('id', id)
      .select(CATEGORY_COLUMNS)
      .single();
    if (error) throw error;
    return this.mapCategory(data as NoteCategoryRow);
  }

  async deleteCategory(id: number): Promise<void> {
    const { error: unlinkError } = await this.client
      .from('notes')
      .update({ category_id: null })
      .eq('category_id', id);
    if (unlinkError) throw unlinkError;

    const { error } = await this.client.from('note_categories').delete().eq('id', id);
    if (error) throw error;
  }

  private mapNote(row: NoteRow): PersonalNote {
    return {
      id: row.id,
      title: row.title,
      content: row.content,
      categoryId: row.category_id,
      noteDate: row.note_date,
      archivedAt: row.archived_at,
      pinnedAt: row.pinned_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }

  private mapCategory(row: NoteCategoryRow): NoteCategory {
    return {
      id: row.id,
      name: row.name,
      domainId: row.domain_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
